/**
 * Split `totalCents` across `weights` proportionally, distributing leftover
 * cents to the largest fractional remainders (ties: earlier entry wins).
 */
export function distributeCentsLargestRemainder(totalCents, weights) {
    if (weights.length === 0)
        return [];
    const weightSum = weights.reduce((s, w) => s + w.weight, 0);
    if (weightSum <= 0 || totalCents === 0) {
        return weights.map((w) => ({ id: w.id, amountCents: 0 }));
    }
    const exact = weights.map((w) => (totalCents * w.weight) / weightSum);
    const floors = exact.map(Math.floor);
    let leftover = totalCents - floors.reduce((s, f) => s + f, 0);
    // Indices ordered by descending fractional remainder; stable for ties.
    const order = exact
        .map((v, i) => ({ i, frac: v - Math.floor(v) }))
        .sort((a, b) => b.frac - a.frac || a.i - b.i);
    const out = floors.slice();
    for (const { i } of order) {
        if (leftover <= 0)
            break;
        out[i] = (out[i] ?? 0) + 1;
        leftover -= 1;
    }
    return weights.map((w, i) => ({ id: w.id, amountCents: out[i] }));
}
/** Autofill per the prescribed rule (design §7): ratio → largest-remainder;
 *  priority → full amount on the first target; no rule → zeroed operator rows. */
export function autofillAllocations(totalCents, cfg) {
    const rule = cfg.rule;
    if (rule?.type === 'ratio') {
        const weights = cfg.targets.map((t) => ({ id: t.id, weight: rule.ratios[t.id] ?? 0 }));
        return distributeCentsLargestRemainder(totalCents, weights).map((r) => ({
            targetId: r.id,
            amountCents: r.amountCents,
            source: 'auto',
        }));
    }
    if (rule?.type === 'priority') {
        return cfg.targets.map((t) => ({
            targetId: t.id,
            amountCents: t.id === rule.order[0] ? totalCents : 0,
            source: 'auto',
        }));
    }
    return cfg.targets.map((t) => ({ targetId: t.id, amountCents: 0, source: 'operator' }));
}
/** The running remainder that must reach ZERO to accept when allocation is
 *  configured (design §7). */
export function allocationRemainderCents(totalCents, rows) {
    return totalCents - rows.reduce((s, r) => s + r.amountCents, 0);
}
//# sourceMappingURL=allocation.js.map